import { useState, useMemo } from 'react';
import { Search, Anchor } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useQuotes } from '../../Hooks/useQuotes';
import RequestCard from './components/RequestCard';
import RequestStats from './components/RequestStats';
import RequestModal from '../model/RequestModal';
import { canAccess } from '../../utils/permissions';
import { ClipboardList } from 'lucide-react';

export default function OpenRequestsPage() {
    const [selectedCategory,] = useState('All');
    const [selectedLocation,] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedQuoteId, setSelectedQuoteId] = useState(null);
    const [showOfferFormDirectly, setShowOfferFormDirectly] = useState(false);

    const { quotes, isLoading } = useQuotes();

    // Check if user can send offers
    const canSendOffer = useMemo(() => {
        return canAccess('send_offer');
    }, []);

    if (!canAccess("open_quote_request")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <ClipboardList className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view open quote requests.
                </p>
            </div>
        );
    }

    const mappedRequests = quotes.map(q => ({
        id: q.id,
        client: `${q.first_name} ${q.last_name}`.trim() || 'Anonymous',
        firstName: q.first_name,
        lastName: q.last_name,
        email: q.email,
        phone: q.phone,
        boatType: q.boat_type,
        boatYear: q.boat_year,
        boatMake: q.boat_make,
        boatLength: q.boat_length?.includes('ft') ? q.boat_length : `${q.boat_length}ft`,
        boatLocation: q.boat_location,
        surveyType: q.service?.name || 'Unknown Service',
        message: q.message || 'No message provided.',
        files: q.files?.length || 0,
        posted: formatDistanceToNow(new Date(q.created_at), { addSuffix: true }),
        claimed: q.status !== 'pending' || q.accepted_company_id !== null,
        raw: q,
    }));

    const filteredRequests = mappedRequests.filter(req => {
        const matchesSearch = req.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.boatMake.toLowerCase().includes(searchTerm.toLowerCase()) ||
            req.surveyType.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCategory = selectedCategory === 'All' || req.surveyType.includes(selectedCategory);
        const matchesLocation = selectedLocation === 'All' || req.boatLocation.toLowerCase().includes(selectedLocation.toLowerCase());

        return matchesSearch && matchesCategory && matchesLocation;
    });

    const availableCount = filteredRequests.filter(r => !r.claimed).length;

    const handleViewDetails = (id) => {
        setSelectedQuoteId(id);
        setShowOfferFormDirectly(false);
    };

    const handleGiveOffer = (id) => {
        setSelectedQuoteId(id);
        setShowOfferFormDirectly(true);
    };

    return (
        <>
            <section className="min-h-screen  text-sm">
                <div className="bg-white border-b border-gray-200 px-4 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Open Survey Requests</h1>
                            <p className="text-xs text-gray-600 mt-1">Claim a yacht survey and grow your business</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by client, make, or survey type..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2.5 w-80 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 text-sm bg-white"
                                />
                            </div>
                        </div>
                    </div>

                    <RequestStats available={availableCount} total={filteredRequests.length} />
                </div>

                {/* Cards */}
                <div className="px-4 py-5 space-y-4">
                    {isLoading ? (
                        <div className="text-center py-20">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
                        </div>
                    ) : filteredRequests.length === 0 ? (
                        <div className="text-center py-20 text-gray-500">
                            <Anchor className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-sm font-medium">No open survey requests</p>
                        </div>
                    ) : (
                        filteredRequests.map((req) => (
                            <RequestCard
                                key={req.id}
                                request={req}
                                onViewDetails={handleViewDetails}
                                onGiveOffer={handleGiveOffer}
                                canGiveOffer={canSendOffer}
                            />
                        ))
                    )}
                </div>
            </section>

            <RequestModal
                quoteId={selectedQuoteId}
                onClose={() => {
                    setSelectedQuoteId(null);
                    setShowOfferFormDirectly(false);
                }}
                forceOfferForm={showOfferFormDirectly}
            />
        </>
    );
}